import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiAlertCircle, FiCheck, FiGlobe, FiLoader, FiSend } from 'react-icons/fi';
import { api } from '../services/api';

const AdminServiceTranslationPublish = ({ serviceId }) => {
  const [loading, setLoading] = useState(true);
  const [locales, setLocales] = useState([]);
  const [translations, setTranslations] = useState([]);
  const [activeLocale, setActiveLocale] = useState('');
  const [publishState, setPublishState] = useState('idle');
  const [pendingCount, setPendingCount] = useState(0);
  const [error, setError] = useState('');
  const pendingFields = useRef(new Set());

  const enabledLocales = useMemo(
    () => locales.filter((locale) => locale.enabled !== false && locale.code !== 'fa'),
    [locales]
  );
  const activeLanguage = enabledLocales.find((locale) => locale.code === activeLocale);
  const activeTranslation = translations.find((item) => item.locale === activeLocale);
  const isStale = Boolean(activeTranslation?.isStale || activeTranslation?.isPublishedStale);

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!serviceId) return;
    if (!quiet) setLoading(true);
    try {
      const [localeResponse, translationResponse] = await Promise.all([
        api.get('/api/locales/admin'),
        api.get(`/api/services/${serviceId}/translations`),
      ]);
      const localeItems = Array.isArray(localeResponse.data) ? localeResponse.data : [];
      const translationItems = Array.isArray(translationResponse.data) ? translationResponse.data : [];
      const available = localeItems.filter((locale) => locale.enabled !== false && locale.code !== 'fa');
      setLocales(localeItems);
      setTranslations(translationItems);
      setActiveLocale((current) => (
        available.some((locale) => locale.code === current) ? current : (available[0]?.code || '')
      ));
    } catch (loadError) {
      setError(loadError.data?.message || loadError.message || 'دریافت وضعیت ترجمه‌های خدمات انجام نشد');
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [serviceId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onSaveState = (event) => {
      const detail = event.detail || {};
      if (detail.entityType !== 'service' || String(detail.entityId) !== String(serviceId)) return;
      const key = `${detail.locale || ''}:${detail.fieldPath || ''}`;
      if (['waiting', 'saving'].includes(detail.state)) pendingFields.current.add(key);
      else pendingFields.current.delete(key);
      setPendingCount(pendingFields.current.size);
    };
    const onTranslationsChanged = (event) => {
      const detail = event.detail || {};
      if (detail.entityType === 'service' && String(detail.entityId) === String(serviceId)) {
        load({ quiet: true });
      }
    };
    window.addEventListener('haryad:translation-save-state', onSaveState);
    window.addEventListener('haryad:translations-changed', onTranslationsChanged);
    return () => {
      window.removeEventListener('haryad:translation-save-state', onSaveState);
      window.removeEventListener('haryad:translations-changed', onTranslationsChanged);
    };
  }, [load, serviceId]);

  const publish = async () => {
    if (!activeLocale || pendingCount || publishState === 'publishing') return;
    setPublishState('publishing');
    setError('');
    try {
      const latestResponse = await api.get(`/api/services/${serviceId}/translations`);
      const latestItems = Array.isArray(latestResponse.data) ? latestResponse.data : [];
      const translation = latestItems.find((item) => item.locale === activeLocale);
      if (!translation) throw new Error('ابتدا دست‌کم یک فیلد این زبان را ترجمه و ذخیره کنید');
      await api.put(`/api/services/${serviceId}/translations/${activeLocale}`, {
        data: translation.data || {},
        slug: translation.slug || '',
        status: 'published',
      });
      await load({ quiet: true });
      setPublishState('published');
      window.dispatchEvent(new CustomEvent('haryad:translations-changed', {
        detail: { entityType: 'service', entityId: serviceId },
      }));
    } catch (publishError) {
      setPublishState('error');
      const missing = Array.isArray(publishError.data?.missingPaths)
        ? ` (${publishError.data.missingPaths.join('، ')})`
        : '';
      setError(`${publishError.data?.message || publishError.message || 'انتشار ترجمه خدمات انجام نشد'}${missing}`);
    }
  };

  if (!serviceId) return null;

  return (
    <section className="service-editor-translation-publish" aria-label="انتشار نهایی ترجمه خدمات">
      <div className="service-editor-translation-publish__heading">
        <span><FiGlobe /></span>
        <div>
          <strong>انتشار ترجمهٔ خدمات</strong>
          <small>فیلدها پیش‌نویس را خودکار ذخیره می‌کنند؛ برای هر زبان فقط همین دکمه انتشار وجود دارد.</small>
        </div>
      </div>

      {loading ? (
        <div className="service-editor-translation-publish__loading"><FiLoader /> در حال دریافت...</div>
      ) : (
        <>
          <div className="service-editor-translation-publish__locales" role="tablist" aria-label="زبان ترجمه خدمات">
            {enabledLocales.map((locale) => {
              const translation = translations.find((item) => item.locale === locale.code);
              const stale = Boolean(translation?.isStale || translation?.isPublishedStale);
              return (
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeLocale === locale.code}
                  className={`${activeLocale === locale.code ? 'is-active' : ''} ${stale ? 'is-stale' : ''}`}
                  onClick={() => {
                    setActiveLocale(locale.code);
                    setPublishState('idle');
                    setError('');
                  }}
                  key={locale.code}
                >
                  <b>{locale.code.toUpperCase()}</b>
                  {locale.nativeName}
                  {translation?.status === 'published' ? <FiCheck /> : null}
                  {stale ? <small>نیاز به بازبینی</small> : null}
                </button>
              );
            })}
          </div>

          <div className="service-editor-translation-publish__status">
            {pendingCount ? <><FiLoader className="is-spinning" /> ابتدا ذخیرهٔ خودکار فیلدها کامل شود.</> : null}
            {!pendingCount && isStale ? <><FiAlertCircle /> نسخهٔ منتشرشده روی سایت باقی می‌ماند؛ پس از بازبینی دوباره منتشر کنید.</> : null}
            {!pendingCount && !isStale && activeTranslation?.hasUnpublishedChanges ? 'تغییرات این زبان در پیش‌نویس ذخیره شده‌اند.' : null}
            {!pendingCount && !isStale && activeTranslation?.status === 'published' && !activeTranslation?.hasUnpublishedChanges ? 'نسخهٔ منتشرشدهٔ این زبان به‌روز است.' : null}
            {!pendingCount && !activeTranslation ? 'هنوز پیش‌نویسی برای این زبان ثبت نشده است.' : null}
            {error ? <strong role="alert">{error}</strong> : null}
          </div>

          <button
            type="button"
            className={`service-editor-translation-publish__button ${publishState === 'publishing' ? 'is-publishing' : ''}`}
            onClick={publish}
            disabled={!activeLanguage || !activeTranslation || pendingCount > 0 || publishState === 'publishing'}
          >
            {publishState === 'publishing' ? <FiLoader className="is-spinning" /> : <FiSend />}
            {publishState === 'published'
              ? `ترجمهٔ ${activeLanguage?.nativeName || activeLocale} منتشر شد`
              : `انتشار نهایی ترجمهٔ ${activeLanguage?.nativeName || activeLocale}`}
          </button>
        </>
      )}
    </section>
  );
};

export default AdminServiceTranslationPublish;
